import React, { useImperativeHandle, forwardRef, useEffect } from "react";
import { useDispatch, useSelector } from "../../../store";
import { Box, Divider, Fab, Grid2, Typography } from "@mui/material";
import { SubmitHandler, useForm, useFieldArray } from "react-hook-form";
import FormTextField from "../../forms/FormTextField";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import { fetchEmailGroups } from "../../../slices/emailGroup";
import NotificationSettings from "../../../types/notificationSettings";
import Button from "../../base/Button";
import { Add, Delete, Info } from "@mui/icons-material";
import { fetchUsers } from "../../../slices/user";
import FormAutocomplete from "../../forms/FormAutocomplete";
import Option from "../../../types/option";

interface FormProps {
  onSave: SubmitHandler<NotificationSettings>;
}

export type EmailFormSubmitHandler = () => void;

const EmailNotifications = forwardRef<EmailFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { onSave } = props;

    const dispatch = useDispatch();
    const { emailGroups } = useSelector((store) => store.emails);
    const { textUserList, userList } = useSelector((store) => store.users);

    const { handleSubmit, control, reset, setValue } =
      useForm<NotificationSettings>({
        mode: "onChange",
        defaultValues: {
          emailNotifications: true,
          emailGroups: emailGroups,
        },
      });

    const { fields, append, remove } = useFieldArray({
      control,
      name: "emailGroups",
    });

    const { loadWithSkeleton } = useSkeletonLoader(false);

    useImperativeHandle(ref, () => handleSubmit(onSave));

    useEffect(() => {
      if (!!emailGroups && emailGroups.length > 0) {
        reset({ emailGroups: emailGroups });
      }
    }, [reset, emailGroups]);

    useEffect(() => {
      dispatch(fetchEmailGroups());
    }, [dispatch]);

    useEffect(() => {
      dispatch(fetchUsers());
    }, [dispatch]);

    const handleRemoveGroup = (index: number) => {
      remove(index);
    };

    const handleAddGroup = () => {
      append({ name: "", emails: [], phoneNumbers: [] });
    };

    return (
      <Box display="flex" flexDirection="row" width="100%" height="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              paddingX={2}
              marginBottom={3}
            >
              <Typography color="text.primary">
                Give your group a name, add emails (any new address or existing
                from registered users), and add phone numbers from verified,
                opted-in users. Use Add Group to create more.
              </Typography>
            </Box>
            {fields.map((group, index) => (
              <React.Fragment key={group.id}>
                <Grid2 container spacing={2} marginBottom={3}>
                  <Grid2
                    size={{ xs: 12, sm: 1 }}
                    display="flex"
                    justifyContent="center"
                  >
                    <Fab
                      size="small"
                      color="error"
                      onClick={() => handleRemoveGroup(index)}
                    >
                      <Delete />
                    </Fab>
                  </Grid2>
                  <Grid2 container size={11}>
                    <Grid2 size={12}>
                      {loadWithSkeleton(
                        <FormTextField
                          label="Group Name"
                          control={control}
                          fieldName={`emailGroups.${index}.name`}
                          required
                        />,
                      )}
                    </Grid2>
                    <Grid2
                      size={{ xs: 12 }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormAutocomplete
                        control={control}
                        fieldName={`emailGroups.${index}.emails`}
                        freeSolo
                        onChange={(val) => {
                          if (Array.isArray(val)) {
                            const newVal = val.map((x) => {
                              return typeof x === "string"
                                ? x
                                : userList?.find((u) => u.detectorId === x.id)
                                    ?.email!;
                            });
                            setValue(`emailGroups.${index}.emails`, newVal);
                          }
                        }}
                        defaultValue={group.emails?.map((e, idx) => {
                          return {
                            id: idx,
                            value: e,
                          } as Option;
                        })}
                        options={
                          userList?.map((opt) => {
                            return {
                              id: opt.detectorId,
                              value: `${opt.firstName} ${opt.lastName} (${opt.email})`,
                            } as Option;
                          }) || []
                        }
                        multiple
                        label={`Emails`}
                      />
                    </Grid2>
                    <Grid2
                      size={{ xs: 12 }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                    >
                      <FormAutocomplete
                        control={control}
                        fieldName={`emailGroups.${index}.phoneNumbers`}
                        options={
                          textUserList?.map((opt) => {
                            return {
                              id: opt.detectorId,
                              value: `${opt.firstName} ${opt.lastName} (${opt.phoneNumber})`,
                            } as Option;
                          }) || []
                        }
                        multiple
                        label={`Phone Numbers`}
                      />
                    </Grid2>
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      alignItems="center"
                      paddingX={2}
                      marginBottom={3}
                    >
                      <Info color="primary" />
                      <Typography variant="caption" color="primary">
                        Only registered users who have opted-in to text
                        messaging and verified their phone number will appear in
                        this list.
                      </Typography>
                    </Box>
                  </Grid2>
                </Grid2>
                <Divider sx={{ marginBottom: 3 }} />
              </React.Fragment>
            ))}
            <Grid2 container spacing={2} marginBottom={3}>
              <Grid2
                size={{ xs: 12, sm: 5 }}
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Button
                  icon={<Add />}
                  color="secondary"
                  onClick={handleAddGroup}
                  label="Add Group"
                  variant="contained"
                />
              </Grid2>
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  },
);

export default EmailNotifications;
